import './commitment-card.scss';

import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Purposes, Text, Variants } from '@sola/ark-core/atoms/text/text.js';
import {
  ActionCard,
  ActionCardBody,
  ActionCardDivider,
  ActionCardFooter,
  ActionCardSection,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import '@sola/locales/formatters.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.js';
import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet';
import {
  CommitmentCardOverrideData,
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from '../../utils/commitments.js';

type WithOverrideData = {
  overrideData: CommitmentCardOverrideData;
  commitment?: GraphQLCommitment | undefined;
};
type WithoutOverrideData = {
  overrideData?: never;
  commitment: GraphQLCommitment;
};

export type CommitmentCardProps = {
  variant?: 'default' | 'compact'; // NOTE: If we ever need one with a header we can add a 'full' variant
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
} & (WithOverrideData | WithoutOverrideData);

const useComponentData = (args: CommitmentCardProps & { t: TFunction }) => {
  const { commitment, overrideData, variant, t } = args;
  const isCompact = variant === 'compact';

  // Title.
  let title = overrideData?.title;
  if (!title && commitment) {
    title = getCommitmentTitle(commitment, t);
  }

  // Label.
  let label = overrideData?.label;
  if (!label && commitment) {
    label = getCommitmentLabel(commitment, t);
  }

  // Rate.
  let rate;
  if (typeof overrideData?.rate === 'string') {
    rate = overrideData.rate;
  } else if (overrideData?.rate || commitment?.rate) {
    rate = t('common:numbers.currency', {
      amount: `${overrideData?.rate ?? commitment?.rate}`,
      useNegativeParens: true,
    });
  }

  // Footer.
  let footer = overrideData?.footer;
  if (!footer && commitment?.isRecurring && commitment?.paymentDateNext) {
    footer = t('my:commitments.nextPayment', {
      date: commitment.paymentDateNext,
    });
  }

  return {
    commitment,
    isRecurring: commitment?.isRecurring,
    isShowingFooter: !isCompact,
    isShowingFrequency: !isCompact && !!commitment?.paymentFrequency,
    isShowingMissedPayment: !isCompact && !!commitment?.hasMissedPayment,
    footer,
    frequencyAbbr: commitment?.paymentFrequency
      ? getFrequencyAbbr(commitment.paymentFrequency, t)
      : undefined,
    label,
    pledge: overrideData?.pledge,
    rate,
    title,
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const CommitmentCard: React.FC<CommitmentCardProps> = (props) => {
  const { t } = useTranslation(['my', 'common']);

  const cd = useComponentData({ t, ...props });
  const { isActive, onActiveChange } = props;

  return (
    <ActionCard>
      <ActionCardSection>
        <ActionCardBody>
          {/* Header */}
          <CommitmentAvatar responsive commitment={cd.commitment} />

          <div className="CommitmentCard__titlegroup">
            {cd.title && (
              <p className="CommitmentCard__title label-potent label-md md:label-lg mb-snug-md">
                {cd.title}
              </p>
            )}

            {cd.label && (
              <p className="CommitmentCard__description label-potent label-sm label-neutral-mild md:label-md">
                {cd.label}
              </p>
            )}
          </div>

          {/* Meta */}
          <div className="flex flex-col gap-snug-lg md:gap-snug-xl items-end text-right">
            {cd.rate && (
              <span className="label-potent label-md md:label-lg">
                <span className="label-neutral-amplified">{cd.rate}</span>

                {cd.isShowingFrequency && cd.frequencyAbbr && (
                  <span className="label-neutral-mild">{cd.frequencyAbbr}</span>
                )}
              </span>
            )}

            {cd.isShowingMissedPayment && (
              <Badge
                label={t('my:commitments.missedPaymentsLabel')}
                color="negative"
              />
            )}
          </div>
        </ActionCardBody>
      </ActionCardSection>

      {/* Footer */}
      {cd.isShowingFooter && (
        <>
          <ActionCardDivider />
          <ActionCardFooter justify="between">
            {!cd.isRecurring && !cd.pledge && (
              <Text variant={Variants.Label} purpose={Purposes.Warning} asChild>
                <span className="flex items-center">
                  <ArkIcon
                    iconName="alert-triangle"
                    className="mr-snug-lg"
                    size="1.25rem"
                  />
                  <span>{t('my:commitments.notRecurring.title')}</span>
                </span>
              </Text>
            )}

            {cd.footer && (
              <span className="label-moderate label-neutral-mild label-sm md:label-md">
                {cd.footer}
              </span>
            )}

            {(cd.commitment || cd.pledge) && (
              <CommitmentDetailsSheet
                pledge={cd.pledge}
                commitment={cd.commitment!}
                isOpen={isActive}
                onOpenChange={onActiveChange}
                openTrigger={
                  <div className="ml-auto">
                    <LinkStyle
                      noUnderline
                      noPadding
                      inline
                      variant="primary"
                      size="lg"
                    >
                      <button type="button" className="CommitmentCard__trigger">
                        {t('commitments.detailsSheet.triggerLabel')}
                      </button>
                    </LinkStyle>
                  </div>
                }
              />
            )}
          </ActionCardFooter>
        </>
      )}
    </ActionCard>
  );
};
